import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, type RefObject, useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink } from '../components/Nav';
import { WaveformIcon } from '../components/icons';
import { Badge, Banner, Button, CopyButton, Loading, Spinner, cx } from '../components/ui';
import { ApiError, errorMessage } from '../lib/api';
import { shareUrl } from '../lib/env';
import { dateTime, timecode } from '../lib/format';
import type { Playback } from '../lib/types';
import { AudioPlayer } from '../player/AudioPlayer';
import { Comments } from '../player/Comments';
import { Player } from '../player/Player';
import { ReactionBar } from '../player/ReactionBar';
import { Transcript } from '../player/Transcript';
import { useReactions } from '../player/useReactions';
import { useViewBeacon } from '../player/useViewBeacon';
import { qk } from '../queries/keys';
import { fetchShare } from '../queries/share';

/**
 * /v/:shareId. The page everybody who is not a Slate user sees.
 *
 * It has to work with no account, on a phone, on the first tap. It also has to
 * work *before the recording is ready*: the share link is handed out the
 * instant recording starts, so arriving early is normal, not an error.
 *
 * `kind` decides which player mounts. An audio note has no HLS ladder and no
 * poster, so it never touches hls.js and never renders a video element with
 * nothing in it. Everything around the player (comments, the view beacon, the
 * copy button) is the same either way.
 */

/**
 * While there is nothing to play yet. With a first frame from the recorder
 * it looks like a video about to start, which is what it is; without one it
 * says so in words.
 */
function ProcessingPanel({ title, audio, posterUrl }: { title: string; audio: boolean; posterUrl: string | null }) {
  return (
    <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
      {posterUrl ? (
        <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : null}
      <div className="clapper-stripes absolute inset-x-0 top-0 h-2 opacity-[0.12]" aria-hidden="true" />
      <div
        className={cx(
          'relative flex w-full flex-col items-center justify-center gap-3 px-6 text-center',
          audio ? 'py-14' : 'aspect-video',
          posterUrl ? 'bg-slate-950/55' : '',
        )}
      >
        <Spinner size={22} className={posterUrl ? 'text-chalk' : 'text-chalk-faint'} />
        <p className="text-h4 text-chalk">{title}</p>
        <p className="max-w-sm text-body text-chalk-dim">
          {posterUrl
            ? 'Almost there. It will start on its own in a moment.'
            : 'This one is still being processed. It usually takes a minute or two. Leave this page open and it will start on its own.'}
        </p>
      </div>
    </div>
  );
}

export function SharePage({ shareId }: { shareId: string }) {
  const { user, loading: authLoading } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const query = useQuery({
    // Keyed on who is asking: the owner gets their original upload while
    // the ladder is cut, a visitor does not. And not before Firebase has
    // said who that is, or the first answer is the visitor's.
    queryKey: [...qk.share(shareId), user?.uid ?? 'anon'],
    queryFn: () => fetchShare(shareId),
    enabled: !authLoading,
    // Poll while the pipeline works, then stop. `false` turns polling off.
    refetchInterval: (result) => {
      const data = result.state.data;
      const busy = data?.status === 'processing' || data?.status === 'uploading';
      // Captions land a little after the video; keep asking until they do.
      const listening = data?.status === 'ready' && data.transcript?.status === 'pending';
      return busy ? 5000 : listening ? 10000 : false;
    },
    retry: (failureCount, error) => !(error instanceof ApiError && error.isNotFound) && failureCount < 2,
  });

  const video = query.data;
  // Your own take is watchable the moment it lands, straight from the
  // original file; the processed version takes over when it exists.
  // Memoised on the URLs, not the object: the player reloads its source
  // whenever this changes, and a fresh object every render would restart
  // the video on each poll.
  // Every poll signs a fresh raw URL. Hold on to the first one so the
  // player is not handed a "new" source every five seconds; it is good for
  // fifteen minutes, and the processed version replaces it long before.
  const heldRawUrl = useRef<string | null>(null);
  if (video?.rawPlaybackUrl && !heldRawUrl.current) heldRawUrl.current = video.rawPlaybackUrl;
  if (video?.playback) heldRawUrl.current = null;
  const rawPlaybackUrl = video?.playback ? null : heldRawUrl.current;
  const rawPosterUrl = video?.posterUrl ?? null;
  const playable: Playback | null = useMemo(
    () =>
      video?.playback ??
      (rawPlaybackUrl
        ? { hlsUrl: null, mp4Url: rawPlaybackUrl, posterUrl: rawPosterUrl, captionsUrl: null }
        : null),
    [video?.playback, rawPlaybackUrl, rawPosterUrl],
  );
  // Older rows predate `kind`; treat anything unexpected as a screen recording,
  // which is what the player has always assumed.
  const isAudio = video?.kind === 'audio';
  const mediaRef: RefObject<HTMLMediaElement | null> = isAudio ? audioRef : videoRef;
  const beacon = useViewBeacon(shareId, Boolean(video));
  // Same access rule as comments, and the same for both audiences: an
  // anonymous viewer on the public link and the signed-in owner are looking at
  // this exact page.
  const reactions = useReactions(shareId, Boolean(video));

  useEffect(() => {
    if (video?.title) document.title = `${video.title} · Slate`;
    return () => {
      document.title = 'Slate';
    };
  }, [video?.title]);

  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="border-b border-hairline">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between gap-3 px-4 sm:px-6">
          <AppLink to={user ? '/' : '/login'} aria-label="Slate">
            <Wordmark />
          </AppLink>
          {user ? (
            <AppLink to="/">
              <Button size="sm">Your library</Button>
            </AppLink>
          ) : (
            <AppLink to="/signup" className="text-caption text-chalk-faint hover:text-chalk-dim">
              Record your own
            </AppLink>
          )}
        </div>
        <ClapperRule />
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
        {query.isLoading ? <Loading label="Loading this recording" /> : null}

        {query.isError ? (
          <div className="mx-auto max-w-md py-16 text-center">
            <h1 className="text-h3 text-chalk">
              {query.error instanceof ApiError && query.error.isNotFound
                ? 'This link does not go anywhere'
                : 'That did not load'}
            </h1>
            <p className="mt-2 text-body text-chalk-dim">
              {query.error instanceof ApiError && query.error.isNotFound
                ? 'The recording may have been deleted, or the link may be private and meant for somebody signed in.'
                : errorMessage(query.error)}
            </p>
            {!user && query.error instanceof ApiError && query.error.isNotFound ? (
              <AppLink to="/login" className="mt-5 inline-block">
                <Button>Sign in and try again</Button>
              </AppLink>
            ) : null}
          </div>
        ) : null}

        {video ? (
          <div className="space-y-6">
            {video.status === 'failed' ? (
              <Banner tone="danger" title="This recording failed to process">
                Something went wrong converting it. If it is yours, the original upload is still
                safe. Get in touch and it can be reprocessed.
              </Banner>
            ) : null}

            {playable ? (
              isAudio ? (
                <AudioPlayer
                  playback={playable}
                  title={video.title}
                  audioRef={audioRef}
                  durationHint={video.durationSec}
                  onProgress={beacon.report}
                  onEnded={beacon.flush}
                  reactions={reactions.reactions}
                />
              ) : (
                <Player
                  playback={playable}
                  title={video.title}
                  videoRef={videoRef}
                  onProgress={beacon.report}
                  onEnded={beacon.flush}
                  reactions={reactions.reactions}
                />
              )
            ) : (
              <ProcessingPanel title={video.title} audio={isAudio} posterUrl={video.posterUrl} />
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-h3 text-chalk">{video.title}</h1>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-chalk-faint">
                  {isAudio ? (
                    <span className="flex items-center gap-1.5">
                      <WaveformIcon size={13} />
                      Audio note
                    </span>
                  ) : null}
                  {video.author?.name ? <span>{video.author.name}</span> : null}
                  <span>{dateTime(video.createdAt)}</span>
                  {video.durationSec ? (
                    <span data-timecode className="font-mono">
                      {timecode(video.durationSec)}
                    </span>
                  ) : null}
                  {video.status !== 'ready' ? (
                    <Badge tone="caution" pulse>
                      Processing
                    </Badge>
                  ) : null}
                </p>
              </div>
              <CopyButton value={shareUrl(shareId)} size="sm" />
            </div>

            <div className="border-t border-hairline pt-5">
              <ReactionBar
                state={reactions}
                mediaRef={mediaRef}
                ready={Boolean(playable)}
              />
            </div>

            {video.transcript ? (
              <div className="border-t border-hairline pt-6">
                <Transcript
                  status={video.transcript.status}
                  captionsUrl={video.transcript.captionsUrl}
                  mediaRef={mediaRef}
                />
              </div>
            ) : null}

            <div className="border-t border-hairline pt-6">
              <Comments shareId={shareId} mediaRef={mediaRef} playerReady={Boolean(playable)} />
            </div>
          </div>
        ) : null}
      </main>

      <footer className="border-t border-hairline">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-5 text-caption text-chalk-faint sm:px-6">
          <span>Recorded with Slate</span>
          <AppLink to="/signup" className="hover:text-chalk-dim">
            Make your own →
          </AppLink>
        </div>
      </footer>
    </div>
  );
}
